import React, { useState, useEffect } from 'react';
import { storage } from '../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import ConsistentLoader from './ConsistentLoader';

const PDFThumbnail = ({ activityId, onClick, pageNumber }) => {
  const { user } = useAuth();
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user && (activityId || pageNumber)) {
      loadThumbnail();
    }
  }, [user, activityId, pageNumber]);

  const loadThumbnail = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use pageNumber if provided, otherwise use activityId
      const pageNum = pageNumber || activityId;
      console.log('🔄 Loading activity thumbnail for page:', pageNum);

      // Get activity data from Firestore to use the correct imageUrl
      const { dailyActivityService, findDailyActivityFromList } = await import('../services/dailyActivityService');
      const allActivities = await dailyActivityService.getAllActivities();
      const activity = findDailyActivityFromList(allActivities, pageNum);
      
      if (!activity || !activity.imageUrl) {
        throw new Error(`No activity found or no imageUrl for activity ${pageNum}`);
      }
      
      // Use the imageUrl from Firestore (already a full URL)
      // Add cache-busting parameter to force browser to reload the image
      let url = activity.imageUrl;
      const urlObj = new URL(url);
      urlObj.searchParams.set('v', Date.now()); // Add timestamp for cache-busting
      url = urlObj.toString();
      setThumbnailUrl(url);
      
      // Extract filename from URL for logging
      const urlParts = activity.imageUrl.split('/');
      const filename = urlParts[urlParts.length - 1].split('?')[0];
      console.log('✅ Activity thumbnail loaded for page', pageNum, ':', filename);
      
    } catch (error) {
      console.log('❌ Error loading thumbnail:', error.message);
      setError('No activity available');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg p-6 border-2 border-amber-300 flex items-center justify-center min-h-48">
        <ConsistentLoader size="small" showBackground={false} />
      </div>
    );
  }

  if (error || !thumbnailUrl) {
    return (
      <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg p-6 border-2 border-amber-300 hover:border-amber-400 transition-all duration-300 cursor-pointer min-h-48 flex items-center justify-center" onClick={onClick}>
        <div className="text-center">
          <div className="text-4xl mb-3">📄</div>
          <p className="text-amber-700 font-medium text-sm">Click to view activity</p>
          <p className="text-amber-600 text-xs mt-1">Tap to expand and see the full activity</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative group cursor-pointer w-full" onClick={onClick}>
      <div className="rounded-lg border border-amber-300 hover:border-amber-400 transition-all duration-300 overflow-hidden shadow-lg w-full">
        <img
          src={thumbnailUrl}
          alt={`Activity ${activityId} Preview`}
          className="w-full h-64 object-cover object-left"
          onContextMenu={(e) => e.preventDefault()}
          draggable={false}
          onError={(e) => {
            console.log('❌ Error loading PDF thumbnail image');
            e.target.style.display = 'none';
            setError('Image failed to load');
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <div className="bg-white/90 rounded-full p-2 shadow-lg">
            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFThumbnail;
